import { Tree } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as GeoTagIcon } from '../../../../../../../../resource/GeoTagIcon.svg';
import { useWorkScheduleAddressDirectoryQuery } from '../../../../api/useWorkScheduleAddressDirectoryQuery';
import { useWorkScheduleCitiesQuery } from '../../../../api/useWorkScheduleCitiesQuery';
import styles from '../../ScheduleCalendar.module.scss';
import {
  getPreviewKey,
  getScheduleAddressQueryParams,
  getScheduleAddressSummary,
} from '../../utils/scheduleCalendarUtils';

const { TreeNode } = Tree;

const ScheduleAddressPreviewTitle = ({ children, onClick }) => {
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onClick(event);
  };

  return (
    <div
      className={styles.scheduleAddressPreviewToggle}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

const ScheduleAddressTreePreview = ({
  allCompanyAddresses,
  allHouseItems = [],
  cityItems = [],
  cityRegionItems = [],
  houseItems = [],
  streetItems = [],
}) => {
  const getHouseStreetKey = useCallback(
    (houseItem) =>
      String(
        houseItem.streetId ??
          (streetItems.length === 1 ? streetItems[0].value : '')
      ),
    [streetItems]
  );
  const houseItemsByStreetKey = useMemo(() => {
    const map = new Map();

    houseItems.forEach((houseItem) => {
      const streetKey = getHouseStreetKey(houseItem);
      const houses = map.get(streetKey) || [];

      map.set(streetKey, [...houses, houseItem]);
    });

    return map;
  }, [getHouseStreetKey, houseItems]);
  const allHouseItemsByStreetKey = useMemo(() => {
    const map = new Map();

    allHouseItems.forEach((houseItem) => {
      const streetKey = getHouseStreetKey(houseItem);
      const houses = map.get(streetKey) || [];

      map.set(streetKey, [...houses, houseItem]);
    });

    return map;
  }, [allHouseItems, getHouseStreetKey]);
  const initialExpandedKeys = useMemo(() => {
    if (!cityItems.length) {
      return streetItems.map((streetItem, streetIndex) =>
        getPreviewKey('schedule-address-street', streetItem.value, streetIndex)
      );
    }

    return cityItems.flatMap((cityItem, cityIndex) => {
      const cityKey = getPreviewKey(
        'schedule-address-city',
        cityItem.value,
        cityIndex
      );
      const streetKeys = streetItems.map((streetItem, streetIndex) =>
        getPreviewKey(`${cityKey}-street`, streetItem.value, streetIndex)
      );

      return [cityKey, ...streetKeys];
    });
  }, [cityItems, streetItems]);
  const [expandedKeys, setExpandedKeys] = useState(initialExpandedKeys);

  useEffect(() => {
    setExpandedKeys(initialExpandedKeys);
  }, [initialExpandedKeys]);

  const togglePreviewNode = useCallback((nodeKey) => {
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(nodeKey)
        ? prevKeys.filter((key) => key !== nodeKey)
        : [...prevKeys, nodeKey]
    );
  }, []);

  const handleTitleClick = useCallback(
    (event, nodeKey) => {
      event.stopPropagation();
      togglePreviewNode(nodeKey);
    },
    [togglePreviewNode]
  );

  const renderHouse = (houseItem, houseIndex, prefix) => (
    <TreeNode
      key={getPreviewKey(`${prefix}-house`, houseItem.value, houseIndex)}
      title={
        <div className={styles.scheduleAddressPreviewLeaf}>
          {houseItem.label}
        </div>
      }
    />
  );

  const renderCityRegion = (cityRegionItem, cityRegionIndex, prefix) => (
    <TreeNode
      key={getPreviewKey(
        `${prefix}-city-region`,
        cityRegionItem.value,
        cityRegionIndex
      )}
      title={
        <div className={styles.scheduleAddressPreviewLeaf}>
          Микрорайон: {cityRegionItem.label}
        </div>
      }
    />
  );

  const renderStreet = (streetItem, streetIndex, prefix) => {
    const streetKey = getPreviewKey(prefix, streetItem.value, streetIndex);
    const streetHouseItems =
      houseItemsByStreetKey.get(String(streetItem.value)) || [];
    const streetHouseValueSet = new Set(
      streetHouseItems.map((houseItem) => String(houseItem.value))
    );
    const allStreetHouseItems =
      allHouseItemsByStreetKey.get(String(streetItem.value)) || [];
    const hasAllStreetHousesSelected =
      Boolean(allStreetHouseItems.length) &&
      allStreetHouseItems.every((houseItem) =>
        streetHouseValueSet.has(String(houseItem.value))
      );

    return (
      <TreeNode
        key={streetKey}
        title={
          <ScheduleAddressPreviewTitle
            onClick={(event) => handleTitleClick(event, streetKey)}
          >
            <div className={styles.scheduleAddressPreviewNode}>
              {streetItem.label}
            </div>
          </ScheduleAddressPreviewTitle>
        }
      >
        {hasAllStreetHousesSelected ? (
          <TreeNode
            key={`${streetKey}-all-houses`}
            title={
              <div className={styles.scheduleAddressPreviewLeaf}>Все дома</div>
            }
          />
        ) : streetHouseItems.length ? (
          streetHouseItems.map((houseItem, houseIndex) =>
            renderHouse(houseItem, houseIndex, streetKey)
          )
        ) : (
          <TreeNode
            disabled
            key={`${streetKey}-empty`}
            title={
              <div className={styles.scheduleAddressPreviewEmpty}>
                Дома не выбраны
              </div>
            }
          />
        )}
      </TreeNode>
    );
  };

  if (allCompanyAddresses) {
    return (
      <div className={styles.scheduleAddressPreviewEmpty}>Все адреса УК</div>
    );
  }

  if (
    !cityItems.length &&
    !cityRegionItems.length &&
    !streetItems.length &&
    !houseItems.length
  ) {
    return (
      <div className={styles.scheduleAddressPreviewEmpty}>
        Адреса для этого графика работы не указаны
      </div>
    );
  }

  return (
    <div className={styles.scheduleAddressPreviewScroll}>
      <Tree
        className={styles.scheduleAddressPreviewTree}
        expandedKeys={expandedKeys}
        selectable={false}
        onExpand={setExpandedKeys}
      >
        {!cityItems.length && (
          <>
            {cityRegionItems.map((cityRegionItem, cityRegionIndex) =>
              renderCityRegion(
                cityRegionItem,
                cityRegionIndex,
                'schedule-address'
              )
            )}
            {streetItems.length
              ? streetItems.map((streetItem, streetIndex) =>
                  renderStreet(
                    streetItem,
                    streetIndex,
                    'schedule-address-street'
                  )
                )
              : houseItems.map((houseItem, houseIndex) =>
                  renderHouse(houseItem, houseIndex, 'schedule-address')
                )}
          </>
        )}
        {cityItems.map((cityItem, cityIndex) => {
          const cityKey = getPreviewKey(
            'schedule-address-city',
            cityItem.value,
            cityIndex
          );
          const cityStreetItems =
            cityItems.length === 1
              ? streetItems
              : streetItems.filter(
                  (streetItem) =>
                    streetItem.cityId === undefined ||
                    String(streetItem.cityId) === String(cityItem.value)
                );

          return (
            <TreeNode
              key={cityKey}
              title={
                <ScheduleAddressPreviewTitle
                  onClick={(event) => handleTitleClick(event, cityKey)}
                >
                  <div className={styles.scheduleAddressPreviewTitle}>
                    {cityItem.label}
                  </div>
                </ScheduleAddressPreviewTitle>
              }
            >
              {cityRegionItems.map((cityRegionItem, cityRegionIndex) =>
                renderCityRegion(cityRegionItem, cityRegionIndex, cityKey)
              )}
              {cityStreetItems.length
                ? cityStreetItems.map((streetItem, streetIndex) =>
                    renderStreet(streetItem, streetIndex, `${cityKey}-street`)
                  )
                : !cityRegionItems.length && (
                    <TreeNode
                      disabled
                      key={`${cityKey}-empty`}
                      title={
                        <div className={styles.scheduleAddressPreviewEmpty}>
                          Улицы не выбраны
                        </div>
                      }
                    />
                  )}
            </TreeNode>
          );
        })}
      </Tree>
    </div>
  );
};

export const ScheduleAddressesSummary = ({
  companyId,
  schedule,
  periodText,
}) => {
  const { companyId: scheduleCompanyId, cityIds } = useMemo(
    () => getScheduleAddressQueryParams(schedule),
    [schedule]
  );
  const addressCompanyId = scheduleCompanyId || companyId;
  const citiesQuery = useWorkScheduleCitiesQuery({
    companyId: addressCompanyId,
  });
  const addressDirectoryQuery = useWorkScheduleAddressDirectoryQuery(
    { companyId: addressCompanyId, cityId: cityIds[0] },
    {
      enabled: Boolean(cityIds.length),
    }
  );
  const addressDirectory = addressDirectoryQuery.data || {};
  const {
    allCompanyAddresses,
    allHouseItems,
    cityItems,
    cityRegionItems,
    houseItems,
    streetItems,
  } = useMemo(
    () =>
      getScheduleAddressSummary(schedule, {
        cities: citiesQuery.data || [],
        cityRegions: addressDirectory.cityRegions || [],
        streets: addressDirectory.streets || [],
        houses: addressDirectory.houses || [],
      }),
    [
      addressDirectory.houses,
      addressDirectory.cityRegions,
      addressDirectory.streets,
      citiesQuery.data,
      schedule,
    ]
  );

  if (!schedule) {
    return null;
  }

  return (
    <div className={styles.scheduleAddresses}>
      <div className={styles.scheduleAddressesHeader}>
        <div className={styles.scheduleAddressesTitleRow}>
          <GeoTagIcon className={styles.scheduleAddressesIcon} />
          <div>
            <div className={styles.scheduleAddressesTitle}>Адреса графика</div>
            <div className={styles.scheduleAddressesMeta}>
              {periodText || 'Период не указан'}
            </div>
          </div>
        </div>
      </div>
      <ScheduleAddressTreePreview
        allCompanyAddresses={allCompanyAddresses}
        allHouseItems={allHouseItems}
        cityItems={cityItems}
        cityRegionItems={cityRegionItems}
        houseItems={houseItems}
        streetItems={streetItems}
      />
    </div>
  );
};
